import {
  MDSActionMenu,
  MDSActionMenuItem,
  MDSMenuItemDivider,
  MDSIconKind,
  MDSActionMenuProps,
  MDSMenuItemHeader,
} from "@/mds";
import { useAppStore } from "@/store";
import { SettingsTabId } from "@/store/pages/SettingsPageStore/types";
import { observer } from "mobx-react-lite";
import { APP_CONFIG } from "@/domains/global/config";
import { windowModule } from "@/modules/window";
import { isWin } from "@/domains/platform/isWin";
import { clientEnvModule } from "@/modules/client-env";
import { trackEvent } from "@/domains/metrics/segment";
import { TrackedEvent } from "@/domains/metrics/tracked-event";
import { SidebarDropdownThemeSwitcher } from "./sidebar-dropdown-theme-switcher";
import { SidebarDropdownAccountInfo } from "./sidebar-dropdown-account-info";
import { SidebarDropdownSyncStatus } from "./sidebar-dropdown-sync-status";
import { uuidModule } from "@/modules/uuid";
import { UpdateCollectionOperation } from "@/store/sync/operations/collections/UpdateCollectionOperation";
import { TrashNoteOperation } from "@/store/sync/operations/notes/TrashNoteOperation";
import { resolveHotkey } from "@/modules/keyboard-shortcuts/platform";
import { KEYBOARD_SHORTCUT_BINDINGS } from "@/modules/keyboard-shortcuts/registry";
import { MDSShortcutKeyCombo } from "@/mds/components/shortcut-key";

interface SidebarDropdownMenuProps extends MDSActionMenuProps {
  displayAccountInfo?: boolean;
  setIsImportModalOpen: (b: boolean) => void;
  onClick?: () => void;
}

export const SidebarDropdownMenu = observer(
  ({ setIsImportModalOpen, onClick, displayAccountInfo, ...props }: SidebarDropdownMenuProps) => {
    const { store } = useAppStore();
    const isWindows = isWin();
    const isDesktop = clientEnvModule.isDesktop();
    const isDebugMode = store.debug.debugModeEnabled;
    const isNoteContentPreloadingDisabled = store.noteContentDocuments.isPreloadingDisabled;

    return (
      <MDSActionMenu {...props}>
        {displayAccountInfo && (
          <>
            <SidebarDropdownAccountInfo />
            <MDSMenuItemDivider />
          </>
        )}

        <MDSActionMenuItem
          id="import-notes"
          label="Import notes"
          icon={MDSIconKind.Import}
          onClick={() => {
            onClick?.();
            trackEvent(TrackedEvent.ImportViaUserProfileMenu);
            setIsImportModalOpen?.(true);
          }}
        />
        <MDSMenuItemDivider />
        <SidebarDropdownThemeSwitcher />
        <MDSActionMenuItem
          id="settings"
          label="Settings"
          icon={MDSIconKind.Settings}
          onClick={() => {
            onClick?.();
            store.settingsModal.open();
          }}
        />
        <MDSActionMenuItem
          id="view-trash"
          label="Trash"
          icon={MDSIconKind.Trash}
          onClick={() => {
            onClick?.();
            store.navigation.goToTrash();
          }}
        />
        <MDSActionMenuItem
          id="sign-out"
          label="Sign out"
          icon={MDSIconKind.LogOut}
          onClick={async () => {
            onClick?.();
            await store.sync.logout();
            store.navigation.goToLogOut();
          }}
        />

        <MDSMenuItemDivider />
        {!isDesktop && (
          <MDSActionMenuItem
            id="get-desktop-app"
            label={`Download ${isWindows ? "Windows" : "Mac"} app`}
            icon={MDSIconKind.Laptop}
            onClick={() => {
              onClick?.();
              windowModule.openInNewTab({
                url: new URL(isWindows ? APP_CONFIG.URLS.WINDOWS_APP : APP_CONFIG.URLS.MAC_APP),
              });
            }}
          />
        )}
        <MDSActionMenuItem
          id="get-ios-app"
          label="Download iOS app"
          icon={MDSIconKind.PhoneSms}
          onClick={() => {
            onClick?.();
            const url = isDesktop ? APP_CONFIG.URLS.IOS_APP_GUIDE : APP_CONFIG.URLS.IOS_APP_V2;
            windowModule.openInNewTab({ url: new URL(url) });
          }}
        />
        <MDSActionMenuItem
          id="get-chrome-extension"
          label="Install Chrome extension"
          icon={MDSIconKind.PuzzlePiece}
          onClick={async () => {
            onClick?.();
            store.settingsModal.open(SettingsTabId.ChromeExtensionPage);
          }}
        />
        <MDSMenuItemDivider />
        {isDebugMode && (
          <MDSActionMenuItem
            id="keyboard-shortcuts"
            label="Keyboard shortcuts"
            icon={MDSIconKind.Keyboard}
            rightAddon={() => (
              <MDSShortcutKeyCombo
                hotkey={resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.see_keyboard_shortcuts)}
              />
            )}
            onClick={() => {
              onClick?.();
              store.interface.openKeyboardShortcutsModal();
            }}
          />
        )}
        <MDSActionMenuItem
          id="help-and-support"
          label="Help and support"
          icon={MDSIconKind.Help}
          onClick={() => {
            onClick?.();
            windowModule.openInNewTab({ url: new URL(APP_CONFIG.URLS.HELP_AND_SUPPORT) });
          }}
        />

        <MDSMenuItemDivider />
        <SidebarDropdownSyncStatus />

        {isDebugMode && (
          <>
            <MDSMenuItemDivider />
            <MDSMenuItemHeader label="Admin Features" />
            <MDSActionMenuItem
              id="debug-mode-panel"
              label="Admin Panel"
              icon={MDSIconKind.GearCode}
              onClick={() => {
                onClick?.();
                store.navigation.goToAdmin();
              }}
            />
            <MDSActionMenuItem
              id="debug-mode-sessions"
              label="Sessions"
              icon={MDSIconKind.Robot}
              onClick={() => {
                onClick?.();
                store.navigation.goToSessions();
              }}
            />
            <MDSActionMenuItem
              id="debug-mode-mem-agent"
              label="Mem Agent"
              icon={MDSIconKind.Robot}
              onClick={() => {
                onClick?.();
                store.navigation.goToAgent();
              }}
            />
            <MDSActionMenuItem
              id="debug-mode-audio-recordings"
              label="Audio Recordings"
              icon={MDSIconKind.Microphone}
              onClick={() => {
                onClick?.();
                store.navigation.goToAudioRecordings();
              }}
            />
            <MDSActionMenuItem
              id="toggle-queue-debug"
              label="Toggle queue debug panel"
              icon={MDSIconKind.Question}
              onClick={() => {
                onClick?.();
                store.sync.actionQueue.toggleDebugging();
              }}
            />
            <MDSActionMenuItem
              id="toggle-note-content-preloading"
              label={
                isNoteContentPreloadingDisabled
                  ? "Enable note content preloading"
                  : "Disable note content preloading"
              }
              icon={MDSIconKind.Question}
              onClick={() => {
                onClick?.();
                store.noteContentDocuments.togglePreloading();
              }}
            />
            <MDSActionMenuItem
              id="reset-sync"
              label="Reset Sync"
              icon={MDSIconKind.Warning}
              onClick={async () => {
                await store.resetStorageAndReload();
              }}
            />
            <MDSActionMenuItem
              id="pause-sync"
              label="Pause Sync"
              icon={MDSIconKind.Exit}
              onClick={() => {
                store.sync.actionQueue.pause();
              }}
            />
            <MDSActionMenuItem
              id="resume-sync"
              label="Resume Sync"
              icon={MDSIconKind.Play}
              onClick={() => {
                store.sync.actionQueue.resume();
              }}
            />
            <MDSActionMenuItem
              id="push-failed-sync-action"
              label="Push failed sync operation"
              icon={MDSIconKind.Warning}
              onClick={() => {
                const operation = new TrashNoteOperation({
                  store,
                  payload: { id: "invalid-id" },
                });
                store.sync.actionQueue.push(operation);
                onClick?.();
              }}
            />
            <MDSActionMenuItem
              id="push-rejected-sync-operation"
              label="Push permission denied sync operation"
              icon={MDSIconKind.Warning}
              onClick={() => {
                const operation = new UpdateCollectionOperation({
                  store,
                  payload: {
                    id: uuidModule.generate(),
                    title: "Test Title",
                    description: "Test Description",
                  },
                });
                store.sync.actionQueue.push(operation);
                onClick?.();
              }}
            />
          </>
        )}
      </MDSActionMenu>
    );
  }
);
